import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, Link, useLocation } from 'react-router-dom';
import Immutable from 'immutable';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

import {
  UI_OPERATION_STATUS_SUCCESS,
  UI_OPERATION_STATUS_ERROR,
  setOperationStatus,
  UI_OPERATION_STATUS_REQUEST,
} from '@uncinc/react-drupal-core';

import {
  FormTextField,
  Button,
  Container,
  FormRadioButtons,
  FormCheckbox,
  FormSelect,
} from '@uncinc/uncinc-react-kitchen-sink';

import { commerceCartOrderSetAllInformation } from '../../../store/actions/Commerce';
import CustomFormikWrapper from '../../../components/CustomFormikWrapper';
import PostalCodeAutocomplete from '../../../components/Form/PostalCodeAutocomplete';
import StepsProgressBar from '../../../components/StepsProgressBar';
import CartSummary from '../CartSummary';
import { createOrUpdateUserProfile } from '../../../store/actions/Profile';
import { formatCartItemsForAnalytics } from '../../../helpers/commerce';
import { clearPageInformation, setPageInformation, setPageViewSent } from '../../../store/actions/UI';

import '../style.scss';
import './style.scss';
import { DEFAULT_LANGUAGE } from '../../../config/constants';

const AddressSelector = (props) => {
  const { addressesRaw, setFieldValue, organisationField, firstNameField, lastNameField, postalCodeField, localityField, addressField, addressField2 } = props;

  const selectAddress = (index) => {
    const selectedAddress = Immutable.fromJS(addressesRaw.get(index));
    if (selectedAddress) {
      setFieldValue(organisationField, selectedAddress.get('organisation', ''));
      setFieldValue(firstNameField, selectedAddress.get('given_name', ''));
      setFieldValue(lastNameField, selectedAddress.get('family_name', ''));
      setFieldValue(postalCodeField, selectedAddress.get('postal_code', ''));
      setFieldValue(localityField, selectedAddress.get('locality', ''));
      setFieldValue(addressField, selectedAddress.get('address_line1', ''));
      setFieldValue(addressField2, selectedAddress.get('address_line2', ''));
    } else {
      setFieldValue(organisationField, '');
      setFieldValue(firstNameField, '');
      setFieldValue(lastNameField, '');
      setFieldValue(organisationField, '');
      setFieldValue(postalCodeField, '');
      setFieldValue(localityField, '');
      setFieldValue(addressField, '');
      setFieldValue(addressField2, '');
    }
  };

  return (
    <FormSelect
      onChange={(e) => selectAddress(e)}
      fieldName="cart_address_selector"
      {...props}
    />
  );
};

const SignupForm = () => {
  const PAGE_TITLE = 'Gegevens | VeiligheidNL';
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation();
  const currentPath = location.pathname;

  const loggedIn = useSelector((state) => state.getIn(['login', 'loggedIn'], false));
  const profileUpdateStatus = useSelector((state) => state.getIn(['ui', 'profile', 'operations', 'update', 'status']));
  const checkoutStatus = useSelector((state) => state.getIn(['ui', 'commerce', 'operations', 'cart_checkout', 'status']));
  const cart = useSelector((state) => state.getIn(['commerce', 'cart'], Immutable.Map()));
  const uid = useSelector((state) => state.getIn(['login', 'uid'], null));
  const totalPrice = useSelector((state) => state.getIn(['commerce', 'cart', 'content', 'attributes', 'order_total', 'subtotal', 'number']));
  const analyticsItems = useSelector((state) => formatCartItemsForAnalytics(state.getIn(['commerce', 'cart', 'products'])));
  const customerEmail = useSelector((state) => state.getIn(['user', uid, 'mail', 0, 'value'], null));
  const debtorNumber = useSelector((state) => state.getIn(['user', uid, 'field_external_relation_number', 0, 'value'], null));
  const debtor = useSelector((state) => state.getIn(['user', uid, 'debtors', debtorNumber], Immutable.Map()));
  const firstName = useSelector((state) => state.getIn(['user', uid, 'profile', 'field_first_name', 0, 'value'], ''));
  const lastName = useSelector((state) => state.getIn(['user', uid, 'profile', 'field_last_name', 0, 'value'], ''));
  const phoneNumber = useSelector((state) => state.getIn(['user', uid, 'profile', 'field_telephone', 0, 'value'], ''));
  const isLoading = (profileUpdateStatus === UI_OPERATION_STATUS_REQUEST && checkoutStatus === UI_OPERATION_STATUS_REQUEST);

  const addressesRaw = useSelector((state) => state.getIn(['user', uid, 'profile', 'address'], Immutable.List()));
  const addresses = useMemo(() => {
    let output = Immutable.Map();
    addressesRaw.forEach((address, index) => {
      if (address.get('locality', '') !== '') {
        output = output.set(`${index}`, [
          address.get('address_line1', ''),
          address.get('address_line2', ''),
          address.get('postal_code', ''),
          address.get('locality', ''),
        ].join(' '));
      }
    });
    return output;
  }, [addressesRaw]);

  useEffect(() => {
    if (loggedIn === false) {
      history.replace('/cart/checkout/login-or-register');
      return;
    }

    if (profileUpdateStatus === UI_OPERATION_STATUS_SUCCESS && checkoutStatus === UI_OPERATION_STATUS_SUCCESS) {
      history.push('/cart/checkout/confirm');
      dispatch(setOperationStatus('user', 'update', null));
      dispatch(setOperationStatus('commerce', 'cart_checkout', null));
    }
  }, [dispatch, history, profileUpdateStatus, checkoutStatus, loggedIn]);

  useEffect(() => {
    dispatch(clearPageInformation());
    dispatch(setPageInformation(PAGE_TITLE));
    dispatch(setPageViewSent(false));
  }, []);

  const initialValues = {
    business_option: 'business',
    email: customerEmail,
    given_name: firstName,
    family_name: lastName,
    invoice_email: debtor.get('invoiceEmail', cart.getIn(['content', 'attributes', 'field_invoice_email'], customerEmail)),
    customer_reference: cart.getIn(['content', 'attributes', 'field_customer_reference'], ''),
    telephone: phoneNumber,
    billing_given_name: '',
    billing_family_name: '',
    billing_organisation: debtor.get('searchName', cart.getIn(['content', 'attributes', 'billing_information', 'address', 'organization'], '')),
    billing_locality: debtor.get('city', cart.getIn(['content', 'attributes', 'billing_information', 'address', 'locality'], '')),
    billing_postal_code: debtor.get('zipCode', cart.getIn(['content', 'attributes', 'billing_information', 'address', 'postal_code'], '')),
    billing_address_line1: debtor.getIn(['addressFormatted', 'streetName'], cart.getIn(['content', 'attributes', 'billing_information', 'address', 'address_line1'], '')),
    billing_address_line2: debtor.getIn(['addressFormatted', 'houseNumber'], cart.getIn(['content', 'attributes', 'billing_information', 'address', 'address_line2'], '')),
    shipping_organisation: '',
    shipping_given_name: '',
    shipping_family_name: '',
    shipping_locality: '',
    shipping_postal_code: '',
    shipping_address_line1: '',
    shipping_address_line2: '',
    'use-default-billing-address': true,
  };

  const SignupSchema = Yup.object().shape({
    business_option: Yup.string().required(t('validation_messages.signup.business_options')),
    email: Yup.string().required(t('validation_messages.signup.email')),
    invoice_email: Yup.string().email(t('validation_messages.signup.invoice_email')).nullable().required(t('validation_messages.signup.invoice_email')),
    customer_reference: Yup.string()
      .max(30, t('validation_messages.signup.reference'))
      .nullable(),
    given_name: Yup.string().required(t('validation_messages.signup.given_name')),
    family_name: Yup.string().required(t('validation_messages.signup.family_name')),
    telephone: Yup.string().trim().required(t('validation_messages.signup.telephone_required'))
      .min(10, t('validation_messages.signup.telephone_min'))
      .max(20, t('validation_messages.signup.telephone_max'))
      .matches('^(?:(?:(?:\\+|00)31|0)(?:\\d-?\\d{8}|\\d\\d-\\d{7})|0\\d{3}-\\d{6})', t('validation_messages.signup.telephone_invalid')),
    billing_given_name: Yup.string().required(t('validation_messages.signup.given_name')),
    billing_family_name: Yup.string().required(t('validation_messages.signup.family_name')),
    billing_organisation: Yup.string().when('business_option', {
      is: (val) => { return val === 'business'; },
      then: Yup.string().required(t('validation_messages.signup.billing_organisation')).max(60),
      otherwise: Yup.string().notRequired(),
    }),
    billing_locality: Yup.string().required(t('validation_messages.signup.billing_locality')),
    billing_postal_code: Yup.string().required(t('validation_messages.signup.billing_postal_code')).matches('^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-zA-Z ._-]{2}$', t('validation_messages.signup.billing_postal_code_invalid')),
    billing_address_line1: Yup.string().required(t('validation_messages.signup.billing_address_line1')),
    billing_address_line2: Yup.string().trim().required(t('validation_messages.signup.billing_address_line2')).matches('^(?:[0-9]{1})', t('validation_messages.signup.housenumber.matches')),
    shipping_organisation: Yup.string().when(['use-default-billing-address', 'business_option'], {
      is: (billing, business) => { return billing === false && business === 'business'; },
      then: Yup.string().required(t('validation_messages.signup.shipping_organisation')),
      otherwise: Yup.string().notRequired(),
    }),
    shipping_locality: Yup.string().when('use-default-billing-address', {
      is: (val) => { return val === false; },
      then: Yup.string().required(t('validation_messages.signup.shipping_locality')),
      otherwise: Yup.string().notRequired(),
    }),
    shipping_postal_code: Yup.string().when('use-default-billing-address', {
      is: (val) => { return val === false; },
      then: Yup.string().required(t('validation_messages.signup.shipping_postal_code')),
      otherwise: Yup.string().notRequired(),
    }),
    shipping_address_line1: Yup.string().when('use-default-billing-address', {
      is: (val) => { return val === false; },
      then: Yup.string().required(t('validation_messages.signup.shipping_address_line1')),
      otherwise: Yup.string().notRequired(),
    }),
    shipping_address_line2: Yup.string().when('use-default-billing-address', {
      is: (val) => { return val === false; },
      then: Yup.string().trim().required(t('validation_messages.signup.shipping_address_line2')).matches('^(?:[0-9]{1})', t('validation_messages.signup.housenumber.matches')),
      otherwise: Yup.string().notRequired(),
    }),
  });

  const onSubmit = (formValues, form) => {
    // send analytics event
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'add_payment_info',
      ecommerce: {
        currency: 'EUR',
        value: totalPrice,
        items: analyticsItems,
      },
    });

    const isBusiness = (formValues.business_option === 'business');
    const billingAddressSame = (formValues['use-default-billing-address'] === true);
    const shippingOrganisation = (billingAddressSame ? formValues.billing_organisation : formValues.shipping_organisation);

    formValues.billing_address_line2 = formValues.billing_address_line2.replace(/ /g, '');
    formValues.shipping_address_line2 = formValues.shipping_address_line2.replace(/ /g, '');

    dispatch(commerceCartOrderSetAllInformation(
      'NL',
      formValues.billing_locality,
      formValues.billing_postal_code,
      formValues.billing_address_line1,
      formValues.billing_address_line2,
      formValues.billing_given_name,
      formValues.billing_family_name,
      isBusiness ? formValues.billing_organisation : null,
      'NL',
      billingAddressSame ? formValues.billing_locality : formValues.shipping_locality,
      billingAddressSame ? formValues.billing_postal_code : formValues.shipping_postal_code,
      billingAddressSame ? formValues.billing_address_line1 : formValues.shipping_address_line1,
      billingAddressSame ? formValues.billing_address_line2 : formValues.shipping_address_line2,
      billingAddressSame ? formValues.billing_given_name : formValues.shipping_given_name,
      billingAddressSame ? formValues.billing_family_name : formValues.shipping_family_name,
      isBusiness ? shippingOrganisation : null,
      formValues.email,
      formValues.invoice_email,
      formValues.customer_reference,
      formValues.telephone,
    ));

    dispatch(createOrUpdateUserProfile({
      field_first_name: [{ value: formValues.given_name }],
      field_last_name: [{ value: formValues.family_name }],
      field_telephone: [{ value: formValues.telephone }],
    }));

    form.setSubmitting(false);
  };

  // Prevent form from being initialized before we have all the required data.
  if (!customerEmail || !cart || !cart.get('content', Immutable.List()) || !cart.get('content', Immutable.List()).count() > 0) {
    return null;
  }

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang: DEFAULT_LANGUAGE,
        }}
      >
        <title>{PAGE_TITLE}</title>
      </Helmet>
      <StepsProgressBar webShopCurrentPath={currentPath} />
      <Container className="signUpForm">
        <Link to="/cart">
          <div className="Cart__backToPage">
            <div className="icon icon-chevron-right" />
            {t('cart.back')}
          </div>
        </Link>

        {(profileUpdateStatus === UI_OPERATION_STATUS_ERROR || checkoutStatus === UI_OPERATION_STATUS_ERROR) && (
          <div className="Form__error">{t('checkout.error_message')}</div>
        )}

        <CustomFormikWrapper
          onSubmit={onSubmit}
          initialValues={initialValues}
          validationSchema={SignupSchema}
        >
          {
            ({ values }) => {
              return (
                <div className="Cart__container">
                  <div className="Cart__content">
                    <fieldgroup>
                      <legend>Mijn informatie</legend>
                      <FormRadioButtons
                        title={null}
                        fieldName="business_option"
                        options={{
                          business: 'Zakelijk',
                          personal: 'Particulier',
                        }}
                        defaultValue="business"
                      />
                    </fieldgroup>
                    <fieldgroup>
                      <legend>Persoonsgegevens</legend>
                      <div className="row">
                        <div className="col-12 col-md-6">
                          <FormTextField
                            title={t('checkout.first_name.label')}
                            fieldName="given_name"
                          />
                        </div>
                        <div className="col-12 col-md-6">
                          <FormTextField
                            title={t('checkout.last_name.label')}
                            fieldName="family_name"
                          />
                        </div>
                        <div className="col-12 col-md-6">
                          <FormTextField
                            title={t('checkout.email.label')}
                            fieldName="email"
                            disabled={customerEmail !== null}
                          />
                        </div>
                        <div className="col-12 col-md-6">
                          <FormTextField
                            title={t('checkout.telephone.label')}
                            fieldName="telephone"
                          />
                        </div>
                      </div>
                    </fieldgroup>
                    <fieldgroup>
                      <legend>Factuuradres</legend>
                      {values['business_option'] === 'business' && (
                        <FormTextField
                          title={t('checkout.organisation.label')}
                          fieldName="billing_organisation"
                          disabled={debtor.get('id', false)}
                        />
                      )}
                      <div className="row">
                        <div className="col-12 col-md-6">
                          <FormTextField
                            title={t('checkout.first_name.label')}
                            fieldName="billing_given_name"
                          />
                        </div>
                        <div className="col-12 col-md-6">
                          <FormTextField
                            title={t('checkout.last_name.label')}
                            fieldName="billing_family_name"
                          />
                        </div>
                      </div>
                      <PostalCodeAutocomplete
                        title={t('checkout.postal_code.label')}
                        fieldName="billing_postal_code"
                        localityField="billing_locality"
                        addressField="billing_address_line1"
                        disabled={debtor.get('id', false)}
                      />
                      <div className="row">
                        <div className="col-12 col-md-9">
                          <FormTextField
                            title={t('checkout.address.label')}
                            fieldName="billing_address_line1"
                            disabled={debtor.get('id', false)}
                          />
                        </div>
                        <div className="col-12 col-md-3">
                          <FormTextField
                            title={t('checkout.house_number.label')}
                            fieldName="billing_address_line2"
                            disabled={debtor.get('id', false)}
                          />
                        </div>
                      </div>
                      <FormTextField
                        title={t('checkout.city.label')}
                        fieldName="billing_locality"
                        disabled={debtor.get('id', false)}
                      />
                      <FormTextField
                        title={t('checkout.invoice_email.label')}
                        fieldName="invoice_email"
                      />
                      <FormTextField
                        title={t('checkout.customer_reference.label')}
                        fieldName="customer_reference"
                      />
                    </fieldgroup>
                    <fieldgroup>
                      <legend>Afleveradres</legend>
                      <FormCheckbox
                        fieldName="use-default-billing-address"
                        title={t('checkout.use_default_billing_address.label')}
                      />
                      <div className={values['use-default-billing-address'] === true ? 'hidden' : ''}>
                        {addresses.count() > 0 && (
                          <AddressSelector
                            title={t('checkout.address_select.label')}
                            options={addresses.toJS()}
                            addressesRaw={addressesRaw}
                            organisationField="shipping_organisation"
                            firstNameField="shipping_given_name"
                            lastNameField="shipping_family_name"
                            postalCodeField="shipping_postal_code"
                            localityField="shipping_locality"
                            addressField="shipping_address_line1"
                            addressField2="shipping_address_line2"
                          />
                        )}
                        {values['business_option'] === 'business' && (
                          <FormTextField
                            title={t('checkout.organisation.label')}
                            fieldName="shipping_organisation"
                          />
                        )}
                        <div className="row">
                          <div className="col-12 col-md-6">
                            <FormTextField
                              title={t('checkout.first_name.label')}
                              fieldName="shipping_given_name"
                            />
                          </div>
                          <div className="col-12 col-md-6">
                            <FormTextField
                              title={t('checkout.last_name.label')}
                              fieldName="shipping_family_name"
                            />
                          </div>
                        </div>
                        <PostalCodeAutocomplete
                          title={t('checkout.postal_code.label')}
                          fieldName="shipping_postal_code"
                          localityField="shipping_locality"
                          addressField="shipping_address_line1"
                        />
                        <div className="row">
                          <div className="col-12 col-md-9">
                            <FormTextField
                              title={t('checkout.address.label')}
                              fieldName="shipping_address_line1"
                            />
                          </div>
                          <div className="col-12 col-md-3">
                            <FormTextField
                              title={t('checkout.house_number.label')}
                              fieldName="shipping_address_line2"
                            />
                          </div>
                        </div>
                        <FormTextField
                          title={t('checkout.city.label')}
                          fieldName="shipping_locality"
                        />
                      </div>
                    </fieldgroup>
                  </div>

                  <div className="Cart_summary">
                    <div className="CartSummary">
                      <div className="CartSummary__wrapper">
                        <div className="CartSummary__title"><strong>{t('cart.your_order')}</strong></div>
                        <CartSummary showProducts />
                      </div>
                      <Button
                        type="submit"
                        className={`Button Button--primary ${isLoading ? 'Button--loading' : ''}`}
                      >
                        {t('cart.further_to_checkout')}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            }
          }
        </CustomFormikWrapper>
      </Container>
    </>
  );
};

export default SignupForm;
